import z from 'zod';

import { companyIdQueryParams, DataResponseSchema } from './index.js';
import { DataTypePropertySchema } from './metadata.js';

export const MetricsIntegrationSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export const MetricsIntegrationsResponseSchema = z.object({
  data: DataResponseSchema(MetricsIntegrationSchema),
});

export const IntegrationQuerySchema = z.object({
  dataSource: z.string().optional(),
  companyId: companyIdQueryParams,
});

export const ConversionMetricsSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export const ConversionQuerySchema = z.object({
  dataSource: z.string().optional(),
  companyId: companyIdQueryParams,
  integrationId: z.string().optional(),
});

export const ConversionMetricsResponseSchema = z.object({
  data: DataResponseSchema(ConversionMetricsSchema),
});

const AggregationTypeSchema = z.enum(['sum', 'none']);

export const MetricSchema = z.object({
  id: z.string(),
  urn: z.string(),
  group: z.string().prefault('metrics'),
  dataType: z.string(),
  name: z.string(),
  companyId: z.number().nullish(),
  isSystem: z.boolean(),
  description: z.string().nullish(),
  dataTypeProperty: DataTypePropertySchema.nullish(),
  integrationId: z.string().nullish(),
  conversionId: z.string().nullish(),
  conversionName: z.string().nullish(),
  pivotMetricId: z.string().nullish(),
  dataSource: z.string().nullish(),
  metricType: z.enum(['default', 'integrated', 'custom', 'imported']),
  formula: z.string().nullish(),
  isFinancial: z.boolean(),
  aggregationType: AggregationTypeSchema.nullish(),
  active: z.boolean().optional(),
  isGoal: z.boolean().optional(),
  importBeyondEndDate: z.boolean().nullish(),
  contexts: z.array(z.string()).optional(),
  globalId: z.number().nullish(),
  createdBy: z.number().nullish(),
  createdAt: z.string(),
  updatedBy: z.number().nullish(),
  updatedAt: z.string().nullish(),
  createdByFullName: z.string().nullish(),
  updatedByFullName: z.string().nullish(),
  globalCreatedAt: z.string().nullish(),
  globalUpdatedBy: z.number().nullish(),
  globalUpdatedAt: z.string().nullish(),
  globalUpdatedByFullName: z.string().nullish(),
  globalCreatedByFullName: z.string().nullish(),
  kpiOptimizationDirection: z.enum(['maximize', 'minimize']).nullish(),
});

export const ResponseDataMetricSchema = z.object({
  data: DataResponseSchema(MetricSchema),
  error: z.any().nullish(),
});

export const GoalMetricSchema = MetricSchema.partial({
  id: true,
  isSystem: true,
  createdAt: true,
});

export const GoalMetricsGroupsSchema = z.object({
  standard: z.array(GoalMetricSchema),
  imported: z.array(GoalMetricSchema),
  adServer: z.array(GoalMetricSchema),
  analytics: z.array(GoalMetricSchema),
  platform: z.array(GoalMetricSchema),
});

export const GoalMetricsOptionsDataSchema = DataResponseSchema(
  GoalMetricsGroupsSchema,
);

export const GoalMetricsOptionsResponseSchema = z.object({
  data: GoalMetricsOptionsDataSchema,
  error: z.any().nullish(),
});

export const GoalMetricsOptionsQuerySchema = z.object({
  companyId: companyIdQueryParams,
  includeDisabled: z.boolean().optional(),
});

export const ListMetricsQuerySchema = z.object({
  companyId: companyIdQueryParams,
  context: z.string().optional(),
  pageNumber: z.number().optional(),
  itemsPerPage: z.number().optional(),
  ordering: z.string().optional(),
  labelOrName: z.string().optional(),
  containsLabelOrName: z.string().or(z.boolean()).optional(),
  description: z.string().optional(),
  containsDescription: z.string().or(z.boolean()).optional(),
  dataTypes: z.array(z.string()).nullish(),
  dataSources: z.array(z.string()).nullish(),
  metricTypes: z.array(z.string()).nullish(),
  formulaMetrics: z.array(z.string()).nullish(),
  active: z.boolean().optional(),
  showGlobals: z.boolean().optional(),
});

export const CreateMetricRequestSchema = z.object({
  group: z.string().prefault('metrics'),
  dataType: z.string(),
  name: z.string().min(1).max(100),
  companyId: z.string().or(z.number()).nullable(),
  description: z.string().nullish(),
  dataTypeProperty: DataTypePropertySchema.nullish(),
  integrationId: z.string().nullable(),
  conversionId: z.string().or(z.number()).nullable(),
  conversionName: z.string().nullish(),
  dataSource: z.string().nullish(),
  metricType: z.enum(['default', 'integrated', 'custom', 'imported']),
  formula: z.string().nullish(),
  isFinancial: z.boolean().prefault(false),
  aggregationType: AggregationTypeSchema.nullish(),
  active: z.boolean().optional(),
  isGoal: z.boolean().prefault(false),
  importBeyondEndDate: z.boolean().nullish(),
  kpiOptimizationDirection: z.enum(['maximize', 'minimize']).nullish(),
});

export const UpdateMetricRequestSchema = z.object({
  name: z.string().max(100).nullish(),
  dataType: z.string(),
  metricType: z.enum(['default', 'integrated', 'custom', 'imported']),
  companyId: z.string().or(z.number()).nullable(),
  description: z.string().nullish(),
  formula: z.string().nullish(),
  dataTypeProperty: DataTypePropertySchema.nullish(),
  active: z.boolean().optional(),
  isGoal: z.boolean(),
  importBeyondEndDate: z.boolean().nullish(),
  kpiOptimizationDirection: z.enum(['maximize', 'minimize']).nullish(),
});

export const OverrideMetricRequestSchema = z.object({
  companyId: z.number().positive(),
  fieldDefinitionId: z.string(),
  metricType: z.enum(['default', 'integrated', 'custom', 'imported']),
  overriddenName: z.string().min(1).max(64),
  active: z.boolean().prefault(true),
  isGoal: z.boolean().prefault(false),
});

export const OverrideMetricResponseSchema = z.object({
  data: DataResponseSchema(MetricSchema).nullish(),
  error: z.any().nullish(),
});

export const MetricOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  fieldDefinitionId: z.string(),
  dataType: z.string().optional(),
});

export const MetricOptionResponseSchema = z.object({
  data: DataResponseSchema(MetricOptionSchema),
});

export const ResponseDataMetricOptionsSchema = z.object({
  data: MetricOptionResponseSchema.nullish(),
  error: z.any().nullish(),
});

export const MetricOptionsQuerySchema = z.object({
  context: z.string().optional(),
  companyId: companyIdQueryParams,
});

export const CheckMetricUniquenessQuerySchema = z.object({
  name: z.string(),
  companyId: companyIdQueryParams,
});

export type MetricType = z.infer<typeof MetricSchema>;
export enum MetricTypeEnum {
  default = 'default',
  integrated = 'integrated',
  custom = 'custom',
  imported = 'imported',
}
export type ResponseDataMetricType = z.infer<typeof ResponseDataMetricSchema>;
export type CreateMetricRequestType = z.infer<typeof CreateMetricRequestSchema>;
export type OverrideMetricRequestType = z.infer<
  typeof OverrideMetricRequestSchema
>;
export type OverrideMetricResponseType = z.infer<
  typeof OverrideMetricResponseSchema
>;
export type GoalMetricType = z.infer<typeof GoalMetricSchema>;
export type GoalMetricsGroupsType = z.infer<typeof GoalMetricsGroupsSchema>;
export type GoalMetricsOptionsDataType = z.infer<
  typeof GoalMetricsOptionsDataSchema
>;
